import { c as _c } from "react/compiler-runtime";
// Thin shadcn-style wrapper over Recharts: a themed container plus a tooltip
// that matches the console's cards. Charts stay declarative at the call site
// and all styling decisions live here.
//
// Kept local to the admin app for now. If seller-web or client-web need
// charts, promote this file to @osmix/ui unchanged.
import type { ComponentProps, ReactNode } from "react";
import { ResponsiveContainer, Tooltip } from "recharts";
import { cn } from "@/lib/utils";

/** Per-series display config, keyed by the series' dataKey. */
export type ChartConfig = Record<string, {
  label: string;
  color: string;
}>;
export function ChartContainer(t0) {
  const $ = _c(10);
  const {
    className,
    height: t1,
    children
  } = t0;
  const height = t1 === undefined ? 220 : t1;
  let t2;
  if ($[0] !== className) {
    t2 = cn("w-full", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== height) {
    t3 = {
      height
    };
    $[2] = height;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== children) {
    t4 = <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>;
    $[4] = children;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = <div className={t2} style={t3}>{t4}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
type TooltipPayloadEntry = {
  dataKey?: string | number;
  name?: string | number;
  value?: number | string;
  color?: string;
};

/**
 * Card-styled tooltip. Pass `formatter` to render values as money, percentages
 * or whatever the series means — the default prints a localised number.
 */
export function ChartTooltip(t0) {
  const $ = _c(5);
  const {
    config,
    formatter,
    labelFormatter
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      stroke: "var(--border)",
      strokeWidth: 1
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== config || $[2] !== formatter || $[3] !== labelFormatter) {
    t2 = <Tooltip cursor={t1} content={t3 => {
      const {
        active,
        payload,
        label
      } = t3;
      if (!active || !payload?.length) {
        return null;
      }
      return <div className="rounded-lg border bg-popover px-3 py-2 text-popover-foreground shadow-md"><div className="mb-1 text-xs font-medium text-muted-foreground">{labelFormatter ? labelFormatter(String(label)) : String(label)}</div><div className="flex flex-col gap-0.5">{(payload as TooltipPayloadEntry[]).map(entry => {
            const key = String(entry.dataKey ?? entry.name ?? "");
            const series = config[key];
            const numeric = typeof entry.value === "number" ? entry.value : Number(entry.value ?? 0);
            return <div key={key} className="flex items-center gap-2 text-sm"><span className="size-2 shrink-0 rounded-full" style={{
                backgroundColor: series?.color ?? entry.color
              }} /><span className="text-muted-foreground">{series?.label ?? key}</span><span className="ml-auto font-medium tabular-nums">{formatter ? formatter(numeric) : numeric.toLocaleString("ru-RU")}</span></div>;
          })}</div></div>;
    }} />;
    $[1] = config;
    $[2] = formatter;
    $[3] = labelFormatter;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/** Shared axis styling so every chart in the console reads the same. */
export const axisProps = {
  stroke: "var(--muted-foreground)",
  fontSize: 12,
  tickLine: false,
  axisLine: false
} as const;
export function ChartLegend(t0) {
  const $ = _c(4);
  const {
    config,
    children
  } = t0;
  if (children) {
    let t1;
    if ($[0] !== children) {
      t1 = <div className="flex flex-wrap gap-4 text-sm">{children}</div>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== config) {
    t1 = <div className="flex flex-wrap gap-4 text-sm">{Object.entries(config ?? {}).map(_temp)}</div>;
    $[2] = config;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function _temp(t0) {
  const [key, series] = t0;
  return <div key={key} className="flex items-center gap-2"><span className="size-2 rounded-full" style={{
      backgroundColor: series.color
    }} /><span className="text-muted-foreground">{series.label}</span></div>;
}